import { FC, useEffect, useMemo } from 'react'
import { useGalleryAssignment, useGalleryConstants, useGalleryProduct, useGalleryVisualsDownloadArchive, useStarRatingAnalyticsData } from 'components/pages/Gallery/_main/contexts'

import { APIRequestState } from 'constants/API'
import { DownloadToastHandler } from 'components/common/Gallery/DownloadToastHandler'
import { StarRatingPopup } from 'components/common/StarRating'
import { useStarRatingEntries } from 'utils/hooks'

/**
 * Gallery download popup component for wrapping and handling download and archive process.
 * Renders the headless DownloadToastHandler plus the StarRatingPopup shown while the archive is prepared.
 * @example <GalleryVisualDownloadPopup />
 */
export const GalleryVisualDownloadPopup: FC = () => {
  const {
    beforeUnloadHandler,
  } = useGalleryConstants()
  const {
    assignmentId,
    isDownloadModalOpen,
    setDownloadModalOpen,
    initiateDownloadVisualsArchive,
    downloadVisualsArchive,
    archiveAdmin,
    downloadVisualsArchiveAdmin
  } = useGalleryVisualsDownloadArchive()

  const {
    assignmentData,
    creative
  } = useGalleryAssignment()

  const { isSelfUploadProduct, isAIEditingProduct } = useGalleryProduct()

  const skipRating = isSelfUploadProduct || isAIEditingProduct

  const archiveDownloadPending = useMemo(() => {
    if (initiateDownloadVisualsArchive) {
      if (initiateDownloadVisualsArchive.state === APIRequestState.ERROR) return false
      if (downloadVisualsArchive?.state === APIRequestState.ERROR) return false
      if (downloadVisualsArchive?.state === APIRequestState.OK) return false
      return true
    } else return false
  }, [initiateDownloadVisualsArchive, downloadVisualsArchive])

  const archiveAdminDownloadPending = useMemo(() => {
    if (archiveAdmin) {
      if (archiveAdmin.state === APIRequestState.ERROR) return false
      if (downloadVisualsArchiveAdmin?.state === APIRequestState.ERROR) return false
      if (downloadVisualsArchiveAdmin?.state === APIRequestState.OK) return false
      return true
    } else return false
  }, [archiveAdmin, downloadVisualsArchiveAdmin])

  // Warn about archive download in progress while leaving the browser tab
  useEffect(() => {
    if (archiveDownloadPending || archiveAdminDownloadPending) {
      window.addEventListener('beforeunload', beforeUnloadHandler)
      return () => {
        window.removeEventListener('beforeunload', beforeUnloadHandler)
      }
    }
  }, [archiveDownloadPending, archiveAdminDownloadPending, beforeUnloadHandler])

  const starRatingAnalyticsData = useStarRatingAnalyticsData()

  // -- Star rating: fetch detailed ratings (with criteria) from the dedicated endpoint
  const { ratings: initialRatings, isFavouriteCT } = useStarRatingEntries(assignmentData?.id)

  return (
    <>

      <DownloadToastHandler assignmentId={assignmentId} initiateDownloadVisualsArchive={initiateDownloadVisualsArchive} downloadVisualsArchive={downloadVisualsArchive} isDownloadTriggered={isDownloadModalOpen} />

      {/* Self-upload / AI editing: no popup — download handled by DownloadToastHandler */}
      {!skipRating && assignmentData &&
        <StarRatingPopup
          isOpen={isDownloadModalOpen}
          onClose={() => setDownloadModalOpen(false)}
          assignmentId={assignmentData.id}
          hasCreative={!!creative}
          creativeName={creative?.name}
          creativeAvatarUrl={undefined}
          initialRatings={initialRatings}
          initialIsFavouriteCT={isFavouriteCT}
          onComplete={(_entries, _avgRating) => setDownloadModalOpen(false)}
          analyticsData={starRatingAnalyticsData}
        />
      }

    </>
  )
}
